/**
 * Consistent Error Handling for XState Services
 *
 * Provides standardized error types and utilities across all service machines.
 */

export interface ServiceError {
  code: string;
  message: string;
  statusCode?: number;
  details?: unknown;
}

export const createError = (
  code: string,
  message: string,
  statusCode?: number,
  details?: unknown
): ServiceError => ({
  code,
  message,
  statusCode,
  details,
});

export const isServiceError = (error: unknown): error is ServiceError => {
  return typeof error === 'object' && error !== null && 'code' in error;
};

/**
 * Normalizes unknown errors to ServiceError format
 */
export const normalizeError = (error: unknown): ServiceError => {
  if (isServiceError(error)) {
    return error;
  }

  if (error instanceof Error) {
    return createError('UNKNOWN_ERROR', error.message, undefined, { stack: error.stack });
  }

  if (typeof error === 'string') {
    return createError('UNKNOWN_ERROR', error);
  }

  return createError('UNKNOWN_ERROR', 'An unexpected error occurred', undefined, {
    originalError: error,
  });
};

/**
 * Common error codes
 */
export const ErrorCodes = {
  NETWORK_ERROR: 'NETWORK_ERROR',
  VALIDATION_ERROR: 'VALIDATION_ERROR',
  NOT_FOUND: 'NOT_FOUND',
  UNAUTHORIZED: 'UNAUTHORIZED',
  FORBIDDEN: 'FORBIDDEN',
  SERVER_ERROR: 'SERVER_ERROR',
  TIMEOUT: 'TIMEOUT',
  UNKNOWN_ERROR: 'UNKNOWN_ERROR',
} as const;
